// src/hooks/useAccessRequest.js
import { useState, useCallback } from 'react';
import { submitAccessRequest, checkExistingRequest } from '../services/api/requestAccessAPI';
import { sanitizeBusinessInfo, validateBusinessInfo } from '../utils/validation';

/**
 * Custom hook for handling access request submissions
 * @returns {Object} - Hook state and methods
 */
export const useAccessRequest = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState(null);
  const [requestId, setRequestId] = useState(null);
  const [hasExistingRequest, setHasExistingRequest] = useState(false);

  /**
   * Submit access request
   * @param {Object} businessData - Business information
   */
  const submitRequest = useCallback(async (businessData) => {
    setIsSubmitting(true);
    setError(null);

    try {
      console.log('Hook: Starting request submission');
      
      // Validate business data
      const validation = validateBusinessInfo(businessData);
      if (!validation.isValid) {
        throw new Error('נתונים לא תקינים: ' + Object.values(validation.errors).join(', '));
      }

      // Sanitize business data
      const sanitizedData = sanitizeBusinessInfo(businessData);
      
      console.log('Hook: Submitting sanitized data', {
        businessName: sanitizedData.businessName,
        businessType: sanitizedData.businessType
      });

      // Submit via API
      const result = await submitAccessRequest(sanitizedData);
      
      if (result.success) {
        setRequestId(result.requestId);
        setIsSubmitted(true);
        console.log('Hook: Request submitted successfully', result.requestId);
      } else {
        throw new Error(result.message || 'שגיאה לא צפויה בשליחת הבקשה');
      }
      
    } catch (err) {
      console.error('Hook: Error submitting request:', err);
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  /**
   * Check for existing request
   */
  const checkExisting = useCallback(async () => {
    setIsChecking(true);
    setError(null);

    try {
      console.log('Hook: Checking for existing request');
      
      const result = await checkExistingRequest();
      
      if (result.success) {
        setHasExistingRequest(result.hasExisting);
        if (result.hasExisting) {
          setRequestId(result.requestId);
        }
        console.log('Hook: Existing request check completed', result.hasExisting);
      }
      
    } catch (err) {
      console.error('Hook: Error checking existing request:', err);
      // Don't set error for this operation as it's not critical
      setHasExistingRequest(false);
    } finally {
      setIsChecking(false);
    }
  }, []);

  /**
   * Reset hook state
   */
  const reset = useCallback(() => {
    setIsSubmitting(false);
    setIsSubmitted(false);
    setIsChecking(false);
    setError(null);
    setRequestId(null);
    setHasExistingRequest(false);
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // State
    isSubmitting,
    isSubmitted,
    isChecking,
    error,
    requestId,
    hasExistingRequest,
    
    // Actions
    submitRequest,
    checkExisting,
    reset,
    clearError
  };
};